import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { Button, Stack } from '@mui/material';
import { useCallback } from 'react';
import type { RVAdvisorInvitationApi } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { InvitationListFrame, InvitationRow, type InvitationStatusConfig } from './advisor-invitation-list-common';

type LocalInvitationStatus = 'pending' | 'accepted' | 'declined';
type LocalInvitationState = Record<number, LocalInvitationStatus>;

const ReceivedInvitations = ({
    invitations,
    loading = false,
    localStatusMap,
    onAcceptInvitation,
    onDeclineInvitation,
    processingInvitationId,
}: {
    invitations: RVAdvisorInvitationApi[];
    loading?: boolean;
    localStatusMap?: LocalInvitationState;
    onAcceptInvitation?: (id: number) => Promise<void>;
    onDeclineInvitation?: (id: number) => Promise<void>;
    processingInvitationId?: number | null;
}) => {
    const getStatusConfig = useCallback(
        (invitation: RVAdvisorInvitationApi): InvitationStatusConfig => {
            const isProcessing = processingInvitationId === invitation.id;

            return {
                pending: {
                    label: (
                        <Stack direction='row' alignItems='center' spacing={1}>
                            <Button
                                variant='outlined'
                                color='secondary'
                                onClick={() => onDeclineInvitation?.(invitation.id)}
                                disabled={isProcessing}
                            >
                                {_('Rechazar')}
                            </Button>
                            <Button
                                variant='contained'
                                color='secondary'
                                onClick={() => onAcceptInvitation?.(invitation.id)}
                                disabled={isProcessing}
                            >
                                {_('Aceptar')}
                            </Button>
                        </Stack>
                    ),
                },
                declined: {
                    label: _('Rechazaste la solicitud'),
                    icon: <PersonRemoveIcon sx={{ width: 20, height: 20 }} color='grey' />,
                },
                accepted: {
                    label: _('Aceptaste la solicitud'),
                    icon: <PersonAddIcon color='success' sx={{ width: 20, height: 20 }} />,
                },
            };
        },
        [onAcceptInvitation, onDeclineInvitation, processingInvitationId]
    );

    return (
        <InvitationListFrame
            loading={loading}
            invitations={invitations}
            emptyIcon={<MoveToInboxIcon sx={{ width: 40, height: 40, color: 'grey' }} />}
            emptyMessage={_('Aún no hay solicitudes de vinculación')}
        >
            {invitations.map((invitation, index) => (
                <InvitationRow
                    key={invitation.id}
                    invitation={invitation}
                    index={index}
                    statusConfig={getStatusConfig(invitation)}
                    overrideStatus={localStatusMap?.[invitation.id]}
                />
            ))}
        </InvitationListFrame>
    );
};

export default ReceivedInvitations;
